import { ReduxControls } from 'rhombus-react';

import { tellSentry } from "utils/Rave";
import { ASSET_TYPES } from 'utils/constants';
import {
	noop,
	isAfterEffectsCreative,
	isImposiumCreative,
	isSocialCreative
} from "utils/common";


/*
	Variables have a `prop` that maps to the Asset's `url` property. In order to update
	image variables on approved creatives we need to pass the `prop` to the Version Variable.
	The `prop` is also used to store the original value of text asset version variables, to
	allow for text asset formatting.
*/
export function mapAssetToVersionVariable(variableName, asset, prop=""){
	const defaultVersionVariables = {
		name:     variableName,
		data:     asset.name,
		type:     asset.assetType,
		assetId:  asset.id,
		filesize: asset.filesize,
	};

	if(asset.assetType === ASSET_TYPES.TEXT){
		return {
			...defaultVersionVariables,
			prop: asset.name,
			isFormatted: false,
			label: `${asset.name} (${asset.id})`,
		};
	}

	if(!!prop) {
		return { ...defaultVersionVariables, prop };
	}

	return defaultVersionVariables;
}


export function getModalProps({ assetType, onClose }){
	return  {
		title:           `Select ${assetType}`,
		type:            "modal",
		className:       "edit-version",
		xClose:          true,
		allowFullScreen: false,
		bladeProps:      { verticallyCenter: true },
		setAppTitle:     false,
		onClose,
	};
}


export function getSelectProps({
	extraSelectProps = {},
	name = "",
	search = noop,
	type,
	videoEncodingFilter = "",
}){
	let isText = type === ASSET_TYPES.TEXT
		, isVideo = type === ASSET_TYPES.VIDEO
		, filter = `eq(assetType,${type})`
		, videoFilter = `all(eq(assetType,${type}),eq(encoded,${videoEncodingFilter}))`
		, defaultSearchParams = {
			rpp:        50,
			sort:       "name:asc",
			searchTerm: "",
			advancedFilter: (isVideo && videoEncodingFilter) ? videoFilter : filter,
		}
		;

	return {
		isAsync:             true,
		isSearchable:        true,
		icon:                "search",
		showIcon:            true,
		label:               name,
		placeholder:         "Search and Select...",
		autoFocus:           true,
		onSelectResetsInput: false,
		defaultOptions:      true,
		loadOptions(val, callback){
			// Returning a value will short circuit the callback flow, so we just call `search`, but don't return its value.
			if(!val){
				search(defaultSearchParams, callback);
			} else {
				search({
					...defaultSearchParams,
					rpp: -1,
					searchTerm: val,
				}, callback);
			}
		},
		normalize(val, previousValue, allValues, previousAllValues, name){
			/*
				The Assets endpoint data and the Version variable data are a little bit mismatched, so we have to normalize to the format that the blade and the API expect.
				Notably, this doesn't update the `prop` value, which is an Asset URL for these variables.
				This shouldn't affect the POST working, so long as the assetId and type are set properly.
			*/
			if(!val){ return previousValue; }

			if(isText){
				if(previousValue.assetId === val.assetId){
					return previousValue;
				}
				return {
					...previousValue,
					...val,
					prop:        val.data,
					isFormatted: false,
				};
			}

			let valName = new RegExp(val.data)
				, propURL = previousValue.prop
				;

			/**
			 * HOPI-1735
			 * 	The ?dirty=true flag does not actually update the `prop` value of version variables
			 * 	When using the drop down, the UI doesn't have the `prop` value
			 * 		This meant when making a preview, the API returned the wrong URL field for the render
			 * 	We shouldn't construct the URL manually because it might not exist, or be correct.
			 * 		It also limits us in case JCRM puts assets into new places
			 * 	We need to replace the prior value with the new value
			 * 		The prior value *might* not be the same value as the current saved value if the user changes the field several times
			 *
			 * 	Temporary workaround until PRVW supports versions
			 * 	Ideally, the API would do a lookup on the `assetId` field and construct the correct value in the response
			 */
			if(!valName.test(previousValue.prop)){
				try {
					propURL = previousValue.prop.replace(previousValue.data, val.data);
				} catch(e){
					tellSentry(`Unable to replace asset URL - ${previousValue.prop} with ${val.data}`);
				}
			}

			return {
				...previousValue,
				data: val.data,
				prop:propURL,
				assetId: val.assetId,
				filesize: val.filesize,
			};
		},

		...extraSelectProps,

		component: ReduxControls.Select,
	};
}

export function shouldUseUnencodedVideos(creative={}) {
	let isAdStudioCreative = /adstudio/gi.test(creative.origin);
	let isDynamicPrerollCreative = /dynamic_preroll/gi.test(creative.adType);
	const isAfterEffects = isAfterEffectsCreative(creative);
	const isSocial = isSocialCreative(creative);
	const isImposium = isImposiumCreative(creative);

	return [
		isAdStudioCreative && isDynamicPrerollCreative,
		isAfterEffects,
		isSocial,
		isImposium
	].some(cond => cond);
}
